import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePremium } from '../hooks/usePremium';
import { trackPremiumClick, trackPaymentStart } from '../utils/analytics';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import {
  Crown, Check, X, Sparkles, Shield, Brain, ClipboardCheck,
  Upload, MessageCircle, TrendingUp, Mail, ExternalLink,
  BarChart3, Target, Lock
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SHOPIER_LINK = 'https://shopier.com/doktorcebinde';

/* ═══════════════════════════════════════════════════════
   1) HERO SECTION
   ═══════════════════════════════════════════════════════ */
const HeroSection = () => (
  <section className="text-center pt-10 pb-8 px-4">
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8" style={{
      background: 'rgba(250,204,21,0.06)', border: '1px solid rgba(250,204,21,0.15)',
    }}>
      <Crown className="w-3.5 h-3.5" style={{ color: '#FACC15' }} />
      <span className="text-xs font-semibold tracking-wide" style={{ color: '#FACC15' }}>Premium</span>
    </div>
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-[1.15] tracking-tight" data-testid="premium-title" style={{
      textShadow: '0 0 60px rgba(255,255,255,0.06)',
    }}>
      Kendi Sağlığınızı Sadece Ölçmeyin<br />
      <span style={{ color: '#67E8F9' }}>Anlayın ve Yönetin</span>
    </h1>
    <p className="text-base sm:text-lg max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
      Yaptığınız testler ve günlük verileriniz analiz edildi. Size özel detaylı değerlendirme Premium'da açılır.
    </p>
  </section>
);

/* ═══════════════════════════════════════════════════════
   2) PERSONALIZED ANALYSIS BLOCK
   ═══════════════════════════════════════════════════════ */
const PersonalAnalysis = ({ summary }) => {
  const bullets = summary?.insight_bullets || [];
  const hasBullets = bullets.length > 0;

  const fallbackBullets = [
    'Verileriniz sisteme kaydedildi ve analiz bekliyor',
    'Detaylı değerlendirme Premium ile açılabilir',
  ];

  const displayBullets = hasBullets ? bullets : fallbackBullets;

  return (
    <section className="max-w-2xl mx-auto px-4 mb-12">
      <div className="relative rounded-2xl overflow-hidden" style={{
        background: 'linear-gradient(170deg, rgba(14,22,45,0.6), rgba(10,16,34,0.7))',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(34,211,238,0.1)',
        boxShadow: '0 0 50px rgba(34,211,238,0.04), 0 20px 60px rgba(0,0,0,0.3)',
      }} data-testid="premium-personal-analysis">
        {/* Top glow line */}
        <div className="absolute top-0 left-10 right-10 h-px" style={{
          background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent)',
        }} />
        <div className="p-7 sm:p-8">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
              background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)',
            }}>
              <Brain className="w-4 h-4" style={{ color: '#67E8F9' }} />
            </div>
            <h2 className="text-base font-bold text-white">Size Özel Analiz Hazır</h2>
          </div>

          <div className="space-y-3 mb-6">
            {displayBullets.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{
                  background: i === 0 ? '#F87171' : i === 1 ? '#FACC15' : '#67E8F9',
                  boxShadow: `0 0 8px ${i === 0 ? 'rgba(248,113,113,0.4)' : i === 1 ? 'rgba(250,204,21,0.4)' : 'rgba(103,232,249,0.4)'}`,
                }} />
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{b}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <Lock className="w-3.5 h-3.5" style={{ color: 'rgba(250,204,21,0.6)' }} />
            <p className="text-xs font-medium" style={{ color: 'rgba(250,204,21,0.7)' }}>
              Bu analizin tamamı Premium üyelikte açılır
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════
   3) ADVANTAGE CARDS GRID
   ═══════════════════════════════════════════════════════ */
const advantages = [
  { icon: Brain, title: 'Detaylı AI Doktor Analizi', desc: 'Neden-sonuç ilişkisi ile tam değerlendirme', color: '#67E8F9' },
  { icon: BarChart3, title: 'Günlük Değişim Takibi', desc: 'Gelişiminizi gün gün görün', color: '#A78BFA' },
  { icon: Target, title: 'Kişisel Görev Planı', desc: 'Size özel egzersizler ve öneriler', color: '#34D399' },
  { icon: Upload, title: 'Belge Analizi', desc: 'Tahlil ve raporlarınızı yorumlayın', color: '#F59E0B' },
  { icon: MessageCircle, title: 'Doktorunuza Sorun', desc: 'Sistemle doğrudan iletişim', color: '#F472B6' },
];

const AdvantageCards = () => (
  <section className="max-w-3xl mx-auto px-4 mb-14">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {advantages.map((a, i) => {
        const Icon = a.icon;
        return (
          <div key={i} className="group rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1" style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.05)',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = `${a.color}22`;
            e.currentTarget.style.boxShadow = `0 0 30px ${a.color}08`;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          data-testid={`advantage-card-${i}`}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-all duration-300" style={{
              background: `${a.color}10`, border: `1px solid ${a.color}20`,
            }}>
              <Icon className="w-5 h-5" style={{ color: a.color }} />
            </div>
            <h3 className="text-sm font-bold text-white mb-1.5">{a.title}</h3>
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>{a.desc}</p>
          </div>
        );
      })}
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════
   4) PSYCHOLOGICAL IMPACT TEXT
   ═══════════════════════════════════════════════════════ */
const PsychBlock = () => (
  <section className="max-w-2xl mx-auto px-4 mb-14 text-center">
    <div className="py-10" style={{ borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <p className="text-lg sm:text-xl font-semibold text-white leading-relaxed" style={{
        textShadow: '0 0 40px rgba(255,255,255,0.04)',
      }} data-testid="premium-psych-text">
        Bu sistem sizi sadece ölçmez.<br />
        <span style={{ color: '#67E8F9' }}>Sizi her gün analiz eder, takip eder ve yönlendirir.</span>
      </p>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════
   5) PROGRESS BAR
   ═══════════════════════════════════════════════════════ */
const ProgressSection = ({ progress }) => (
  <section className="max-w-md mx-auto px-4 mb-14">
    <div className="rounded-2xl p-6 text-center" style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.05)',
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    }} data-testid="premium-progress">
      <p className="text-sm font-semibold text-white mb-4">
        Analiziniz <span style={{ color: '#67E8F9' }}>%{progress}</span> tamamlandı
      </p>
      <div className="relative h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000" style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #22D3EE, #A78BFA)',
          boxShadow: '0 0 12px rgba(34,211,238,0.4)',
        }} />
      </div>
      <p className="text-xs mt-3" style={{ color: 'rgba(255,255,255,0.3)' }}>
        Premium ile analizinizin tamamına erişin
      </p>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════
   6 + 7 + 8) PRICING + CTA + TRUST
   ═══════════════════════════════════════════════════════ */
const PricingSection = ({ isPending, onBuy }) => (
  <section className="max-w-lg mx-auto px-4 mb-10">
    <div className="relative rounded-2xl overflow-hidden" style={{
      background: 'linear-gradient(170deg, rgba(250,204,21,0.05), rgba(245,158,11,0.02))',
      border: '1px solid rgba(250,204,21,0.12)',
      boxShadow: '0 0 60px rgba(250,204,21,0.04)',
    }} data-testid="premium-pricing">
      {/* Top accent */}
      <div className="absolute top-0 left-8 right-8 h-px" style={{
        background: 'linear-gradient(90deg, transparent, rgba(250,204,21,0.3), transparent)',
      }} />
      <div className="p-8 sm:p-10 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#FACC15' }}>
          Aylık Premium Üyelik
        </p>
        <div className="flex items-baseline justify-center gap-1 mb-2">
          <span className="text-5xl sm:text-6xl font-bold text-white" style={{
            textShadow: '0 0 40px rgba(250,204,21,0.1)',
          }}>149</span>
          <span className="text-lg font-medium" style={{ color: '#94A3B8' }}>TL / ay</span>
        </div>
        <p className="text-sm mb-8" style={{ color: 'rgba(255,255,255,0.3)' }}>
          İstediğiniz zaman iptal edebilirsiniz
        </p>

        {/* CTA */}
        {isPending ? (
          <div className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl" style={{
            background: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.2)',
          }}>
            <Sparkles className="w-4 h-4" style={{ color: '#FACC15' }} />
            <span className="text-sm font-medium" style={{ color: '#FACC15' }}>Ödemeniz onay bekliyor...</span>
          </div>
        ) : (
          <button onClick={onBuy}
            className="relative inline-flex items-center gap-2.5 px-10 py-4 rounded text-base font-bold text-black transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #FACC15, #F59E0B)',
              boxShadow: '0 8px 35px rgba(250,204,21,0.35), 0 0 20px rgba(250,204,21,0.12)',
            }}
            data-testid="premium-buy-btn">
            <Crown className="w-5 h-5" />
            Premium Erişimi Başlat
          </button>
        )}

        {/* Trust text */}
        <div className="mt-8 space-y-2">
          {[
            'Ödemeniz onaylandıktan sonra erişiminiz aktif edilir',
            'Genellikle 1–12 saat içinde tamamlanır',
            'Verileriniz güvende kalır',
          ].map((t, i) => (
            <div key={i} className="flex items-center justify-center gap-2">
              <Check className="w-3 h-3 shrink-0" style={{ color: 'rgba(52,211,153,0.5)' }} />
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════
   PAYMENT POPUP — Tek, kapsamlı popup
   ═══════════════════════════════════════════════════════ */
const PaymentPopup = ({ email, loading, onClose, onPay }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = email || '';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}>
      <div className="relative max-w-md w-full rounded-2xl overflow-hidden" style={{
        background: 'rgba(10,18,38,0.97)',
        border: '1px solid rgba(250,204,21,0.12)',
        boxShadow: '0 25px 80px rgba(0,0,0,0.6), 0 0 40px rgba(250,204,21,0.06)',
      }} data-testid="payment-popup">
        {/* Top glow accent */}
        <div className="absolute top-0 left-8 right-8 h-px" style={{
          background: 'linear-gradient(90deg, transparent, rgba(250,204,21,0.25), transparent)',
        }} />

        <div className="p-7 sm:p-8">
          {/* Close */}
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg transition-all hover:bg-white/10"
            data-testid="payment-popup-close">
            <X className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.35)' }} />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{
              background: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.15)',
            }}>
              <Crown className="w-5 h-5" style={{ color: '#FACC15' }} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Premium erişiminizi başlatın</h3>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Kredi kartı ile güvenli ödeme yapabilirsiniz</p>
            </div>
          </div>

          {/* Divider */}
          <div className="my-5 h-px" style={{ background: 'rgba(255,255,255,0.04)' }} />

          {/* EMAIL — EN KRİTİK KISIM */}
          <div className="rounded-xl p-5 mb-4" style={{
            background: 'linear-gradient(170deg, rgba(34,211,238,0.04), rgba(139,92,246,0.03))',
            border: '1px solid rgba(34,211,238,0.1)',
          }}>
            <p className="text-sm font-medium mb-3 text-white">
              Ödeme açıklamasına bu e-posta adresini yazın:
            </p>
            <div className="flex items-center gap-3 rounded-xl p-3.5" style={{
              background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <Mail className="w-5 h-5 shrink-0" style={{ color: '#67E8F9' }} />
              <span className="flex-1 text-base font-bold text-white truncate" data-testid="payment-email" style={{
                letterSpacing: '0.01em',
              }}>
                {email}
              </span>
              <button onClick={handleCopy}
                className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded text-xs font-semibold transition-all hover:scale-105"
                style={{
                  background: copied ? 'rgba(52,211,153,0.15)' : 'rgba(34,211,238,0.1)',
                  border: `1px solid ${copied ? 'rgba(52,211,153,0.3)' : 'rgba(34,211,238,0.2)'}`,
                  color: copied ? '#34D399' : '#67E8F9',
                }}
                data-testid="payment-copy-email">
                {copied ? (
                  <><Check className="w-3 h-3" /> Kopyalandı</>
                ) : (
                  <><ClipboardCheck className="w-3 h-3" /> Kopyala</>
                )}
              </button>
            </div>
            <p className="text-xs mt-3" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Bu bilgi hesabınızın doğru şekilde tanınması için gereklidir
            </p>
          </div>

          {/* CTA Button */}
          <button onClick={onPay} disabled={loading}
            className="w-full flex items-center justify-center gap-2.5 py-4 rounded text-sm font-bold text-black transition-all hover:scale-[1.02] hover:-translate-y-0.5 disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #FACC15, #F59E0B)',
              boxShadow: '0 8px 30px rgba(250,204,21,0.3), 0 0 15px rgba(250,204,21,0.1)',
            }}
            data-testid="payment-go-btn">
            <ExternalLink className="w-4 h-4" />
            {loading ? 'Yönlendiriliyor...' : 'Ödeme Sayfasına Git'}
          </button>

          {/* Trust */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Shield className="w-3.5 h-3.5" style={{ color: 'rgba(52,211,153,0.4)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>Ödeme Shopier güvenli altyapısı ile yapılır</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */
export default function PremiumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPremium, isPending } = usePremium();
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!isPremium) {
      axios.get(`${BACKEND}/api/premium/summary`).then(r => setSummary(r.data)).catch(() => {});
    }
  }, [isPremium]);

  const handleStartPayment = async () => {
    trackPaymentStart();
    setLoading(true);
    try { await axios.post(`${BACKEND}/api/premium/request`, {}); } catch {}
    setLoading(false);
    window.open(SHOPIER_LINK, '_blank');
    navigate('/payment-success');
  };

  /* Already premium */
  if (isPremium) {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <PageSEO title="Premium Üyelik" />
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{
            background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.2)',
            boxShadow: '0 0 30px rgba(250,204,21,0.08)',
          }}>
            <Crown className="w-10 h-10" style={{ color: '#FACC15' }} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Premium Üyesiniz</h1>
          <p className="mb-8" style={{ color: 'rgba(255,255,255,0.45)' }}>Tüm özellikler açık. Sağlığınızı takip etmeye devam edin.</p>
          <button onClick={() => navigate('/dashboard')} className="px-8 py-3 rounded text-sm font-bold text-white transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #22D3EE, #0891B2)', boxShadow: '0 6px 20px rgba(34,211,238,0.3)' }}
            data-testid="premium-go-dashboard">
            Dashboard'a Git
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const progress = summary?.progress || 45;

  return (
    <div className="min-h-screen" style={{ background: 'transparent' }}>
      <PageSEO title="Premium Üyelik" description="AI doktorunuzu tam gücüyle kullanın" />
      <Header />
      <main className="pb-20">
        <HeroSection />
        <PricingSection isPending={isPending} onBuy={() => { trackPremiumClick('premium_page'); setShowPayment(true); }} />
        <PersonalAnalysis summary={summary} />
        <AdvantageCards />
        <PsychBlock />
        <ProgressSection progress={progress} />
      </main>
      <Footer />

      {showPayment && (
        <PaymentPopup
          email={user?.email}
          loading={loading}
          onClose={() => setShowPayment(false)}
          onPay={handleStartPayment}
        />
      )}
    </div>
  );
}
